// Move detection — the novel part of the text engine (the launch hero).
//
// After the Myers line diff, relocated code shows up as a deleted block in one
// place and an inserted block in another. A move may also be edited, so we score
// candidate del/ins block pairs with a fuzzy line alignment and accept only the
// strongest, well-covered pairs.
//
// See DEBUGGING.md RCA-001: similarity is TOKEN-based (not character-based) and
// fuzzy acceptance is deliberately strict to avoid matching unrelated keys.
// Exact unique lines are recovered separately after block matching.
import { lineSimilarity, hasToken, tokenSet } from './normalize.js';
import { inlineDiff } from './inline-diff.js';

const MIN_EDIT_SIM = 0.34; // a diagonal pair must share real tokens to count as a match
const COVERAGE_MIN = 0.5; // matched lines / core length
const MIN_MATCHED = 2; // a move needs at least two aligned lines
const STRENGTH_MIN = 0.5; // average quality of the matched lines
const MAX_BLOCK = 600; // guard against pathologically large fuzzy alignments

export function detectMoves(ops, L, R, norm) {
  const { delBlocks, insBlocks } = extractBlocks(ops);

  // Cheap pre-filter: a block pair that shares NO token can't produce a single
  // matched line (sim ≥ MIN_EDIT_SIM needs token overlap), so skip the O(N·M)
  // Needleman–Wunsch entirely. Behaviour-preserving — it only prunes pairs that
  // could never be accepted. Big win on diffs with many small unrelated blocks.
  for (const d of delBlocks) d.tokens = blockTokens(d.lines, L, norm);
  for (const s of insBlocks) s.tokens = blockTokens(s.lines, R, norm);

  const candidates = [];
  for (const d of delBlocks) {
    if (d.lines.length > MAX_BLOCK) continue;
    for (const s of insBlocks) {
      if (s.lines.length > MAX_BLOCK) continue;
      // Adjacent del→ins (or ins→del) is an in-place replace, not a move.
      if (d.opEnd + 1 === s.opStart || s.opEnd + 1 === d.opStart) continue;
      if (!sharesToken(d.tokens, s.tokens)) continue;
      const ev = evaluate(d, s, L, R, norm);
      if (ev.accept) candidates.push({ d, s, ev });
    }
  }

  candidates.sort((x, y) => y.ev.score - x.ev.score);
  const moves = [];
  for (const c of candidates) {
    if (c.d.move || c.s.move) continue; // one move per block
    const move = makeMove(c.d, c.s, L, R, norm, c.ev);
    c.d.move = move;
    c.s.move = move;
    moves.push(move);
  }
  // After fuzzy block matching, recover exact unique lines even when they
  // are buried among unrelated additions. Repeated/blank lines are ambiguous.
  const frequencies = (lines) => {
    const counts = new Map();
    for (const text of lines) counts.set(text, (counts.get(text) || 0) + 1);
    return counts;
  };
  const lc = frequencies(L), rc = frequencies(R);
  const deletions = new Map();
  for (const block of delBlocks) if (!block.move) {
    block.lines.forEach((index, offset) => {
      const text = L[index];
      if (text.trim() && hasToken(text) && lc.get(text) === 1 && rc.get(text) === 1)
        deletions.set(text, { block, index, offset });
    });
  }
  const exact = new Map();
  for (const block of insBlocks) if (!block.move) {
    block.lines.forEach((index, offset) => {
      const match = deletions.get(R[index]);
      if (!match) return;
      const d = { opStart: match.block.opStart + match.offset, opEnd: match.block.opStart + match.offset, lines: [match.index] };
      const ins = { opStart: block.opStart + offset, opEnd: block.opStart + offset, lines: [index] };
      const move = makeMove(d, ins, L, R, norm, { pairs: [{ li: 0, ri: 0 }], first: 0, last: 0 });
      d.move = ins.move = move;
      exact.set(d.opStart, d);
      exact.set(ins.opStart, ins);
      moves.push(move);
    });
  }
  const split = (blocks) => blocks.flatMap(block => {
    if (block.move) return [block];
    const parts = [];
    for (let offset = 0; offset < block.lines.length; offset++) {
      const op = block.opStart + offset;
      if (exact.has(op)) parts.push(exact.get(op));
      else {
        const last = parts.at(-1);
        if (last && !last.move) { last.lines.push(block.lines[offset]); last.opEnd = op; }
        else parts.push({ opStart: op, opEnd: op, lines: [block.lines[offset]], move: null });
      }
    }
    return parts;
  });
  return { moves, delBlocks: split(delBlocks), insBlocks: split(insBlocks) };
}

// Union of the tokens across a block's lines (on the normalized text).
function blockTokens(lines, side, norm) {
  const set = new Set();
  for (const idx of lines) for (const t of tokenSet(norm(side[idx]))) set.add(t);
  return set;
}

// Do two token sets intersect? Iterate the smaller for a cheap check.
function sharesToken(a, b) {
  const [small, big] = a.size <= b.size ? [a, b] : [b, a];
  for (const t of small) if (big.has(t)) return true;
  return false;
}

function extractBlocks(ops) {
  const delBlocks = [];
  const insBlocks = [];
  let i = 0;
  while (i < ops.length) {
    if (ops[i].op === 'del') {
      const start = i;
      const lines = [];
      while (i < ops.length && ops[i].op === 'del') lines.push(ops[i++].l);
      delBlocks.push({ opStart: start, opEnd: i - 1, lines, move: null });
    } else if (ops[i].op === 'ins') {
      const start = i;
      const lines = [];
      while (i < ops.length && ops[i].op === 'ins') lines.push(ops[i++].r);
      insBlocks.push({ opStart: start, opEnd: i - 1, lines, move: null });
    } else {
      i++;
    }
  }
  return { delBlocks, insBlocks };
}

// Scores a candidate block pair on its aligned core and decides whether it is a
// move. Returns the alignment so makeMove can reuse it.
function evaluate(d, s, L, R, norm) {
  const a = d.lines.map((l) => norm(L[l]));
  const b = s.lines.map((r) => norm(R[r]));
  const { pairs } = align(a, b);

  // The aligned CORE runs between the first and last genuinely similar pair.
  // align() has no gap penalty, so a zero-similarity pair still scores as a
  // diagonal step; counting those at the edges padded coreDel/coreIns with lines
  // that never matched and pushed coverage below COVERAGE_MIN, rejecting real
  // moves. Boundary pairs below MIN_EDIT_SIM belong in the head/tail instead
  // (REL-062). Interior weak pairs still count against coverage.
  let first = -1;
  let last = -1;
  pairs.forEach((p, i) => {
    if (p.li != null && p.ri != null && lineSimilarity(a[p.li], b[p.ri]) >= MIN_EDIT_SIM) {
      if (first < 0) first = i;
      last = i;
    }
  });
  if (first < 0) return { accept: false, score: 0, pairs, first, last };

  let matched = 0;
  let strongSum = 0;
  let substantial = false;
  let coreDel = 0;
  let coreIns = 0;
  for (let i = first; i <= last; i++) {
    const p = pairs[i];
    if (p.li != null) coreDel++;
    if (p.ri != null) coreIns++;
    if (p.li != null && p.ri != null) {
      const sim = lineSimilarity(a[p.li], b[p.ri]);
      if (sim >= MIN_EDIT_SIM) {
        matched++;
        strongSum += sim;
        if (hasToken(a[p.li]) || hasToken(b[p.ri])) substantial = true;
      }
    }
  }
  const coverage = matched / Math.max(coreDel, coreIns);
  const avg = matched ? strongSum / matched : 0;
  const accept = matched >= MIN_MATCHED && coverage >= COVERAGE_MIN && substantial && avg >= STRENGTH_MIN;
  return { accept, score: strongSum, pairs, first, last };
}

function makeMove(d, s, L, R, norm, ev) {
  const { pairs, first, last } = ev;
  const delHead = [];
  const delTail = [];
  const insHead = [];
  const insTail = [];
  const inner = []; // destination (right) rows
  const originRows = []; // origin (left) rows — what moved, shown on the left pane
  let edited = 0;
  let coreFirstL;
  let coreLastL;
  let coreFirstR;
  let coreLastR;

  pairs.forEach((p, idx) => {
    if (idx < first || idx > last) {
      if (p.li != null) (idx < first ? delHead : delTail).push(d.lines[p.li]);
      if (p.ri != null) (idx < first ? insHead : insTail).push(s.lines[p.ri]);
      return;
    }
    if (p.li != null) {
      if (coreFirstL === undefined) coreFirstL = d.lines[p.li];
      coreLastL = d.lines[p.li];
    }
    if (p.ri != null) {
      if (coreFirstR === undefined) coreFirstR = s.lines[p.ri];
      coreLastR = s.lines[p.ri];
    }

    if (p.li != null && p.ri != null) {
      const lIdx = d.lines[p.li];
      const rIdx = s.lines[p.ri];
      if (norm(L[lIdx]) === norm(R[rIdx])) {
        inner.push({ op: 'equal', lnR: rIdx + 1, rText: R[rIdx] });
        originRows.push({ lnL: lIdx + 1, lText: L[lIdx], edited: false });
      } else {
        const inline = inlineDiff(L[lIdx], R[rIdx]);
        inner.push({ op: 'modify', lnR: rIdx + 1, lText: L[lIdx], rText: R[rIdx], inline });
        originRows.push({ lnL: lIdx + 1, lText: L[lIdx], edited: true, inline });
        edited++;
      }
    } else if (p.li != null) {
      const lIdx = d.lines[p.li];
      inner.push({ op: 'del', lText: L[lIdx] });
      originRows.push({ lnL: lIdx + 1, lText: L[lIdx], edited: true });
      edited++;
    } else {
      const rIdx = s.lines[p.ri];
      inner.push({ op: 'ins', lnR: rIdx + 1, rText: R[rIdx] });
      edited++;
    }
  });

  return {
    del: { lStart: coreFirstL, lEnd: coreLastL },
    ins: { rStart: coreFirstR, rEnd: coreLastR },
    delHead,
    delTail,
    insHead,
    insTail,
    editedCount: edited,
    inner,
    originRows,
    originEmitted: false,
    destEmitted: false,
  };
}

// Needleman–Wunsch alignment maximizing the sum of token similarity, with a
// direction matrix for a float-safe backtrace.
function align(a, b) {
  const n = a.length;
  const m = b.length;
  const dp = Array.from({ length: n + 1 }, () => new Array(m + 1).fill(0));
  const dir = Array.from({ length: n + 1 }, () => new Array(m + 1).fill(0)); // 0 diag, 1 up, 2 left
  for (let x = 1; x <= n; x++) {
    for (let y = 1; y <= m; y++) {
      const diag = dp[x - 1][y - 1] + lineSimilarity(a[x - 1], b[y - 1]);
      const up = dp[x - 1][y];
      const left = dp[x][y - 1];
      let best = diag;
      let d = 0;
      if (up > best) {
        best = up;
        d = 1;
      }
      if (left > best) {
        best = left;
        d = 2;
      }
      dp[x][y] = best;
      dir[x][y] = d;
    }
  }

  const pairs = [];
  let x = n;
  let y = m;
  while (x > 0 && y > 0) {
    const d = dir[x][y];
    if (d === 0) {
      pairs.push({ li: x - 1, ri: y - 1 });
      x--;
      y--;
    } else if (d === 1) {
      pairs.push({ li: x - 1, ri: null });
      x--;
    } else {
      pairs.push({ li: null, ri: y - 1 });
      y--;
    }
  }
  while (x > 0) pairs.push({ li: --x, ri: null });
  while (y > 0) pairs.push({ li: null, ri: --y });
  pairs.reverse();
  return { pairs };
}
